// crewbit-admin/src/components/schedule/MonthCell.tsx
//
// Schedule Module Phase C C3 — single calendar cell (admin pair).
// Body parallel to mobile MonthCell.tsx modulo runtime per
// feedback_admin_pair_must_match_mobile_body.md.
//
// Spec: docs/references/schedule/SCHEDULE_MODULE_SPEC_v1.md §4.3 + §11 + §12.

import { palette, admin } from '../../styles/tokens';
import type { ScheduleEntry } from '../../types/schedule';
import type { LeaseBoundaryKind } from '../../hooks/useLeaseBoundariesByMonth';
import { LeaseBoundaryBar } from './LeaseBoundaryBar';
import { RecurringBadge } from './RecurringBadge';

export interface MonthCellProps {
  date: string;
  entries: ScheduleEntry[];
  isCurrentMonth: boolean;
  isToday: boolean;
  density?: 'mobile' | 'desktop';
  onTap: (date: string) => void;
  /** HTML5 drag-drop target — caller computes Δdays + dispatches reschedule RPC. */
  onCellDrop?: (date: string, droppedEntryId: string) => void;
  showRecurringBadges?: boolean;
  leaseBoundary?: LeaseBoundaryKind;
}

function entryPillColor(entry: ScheduleEntry): string {
  if (entry.status === 'cancelled') return palette.g300;
  if (entry.status === 'completed') return palette.g500;
  return palette.accent;
}

export function MonthCell({
  date,
  entries,
  isCurrentMonth,
  isToday,
  density = 'desktop',
  onTap,
  onCellDrop,
  showRecurringBadges,
  leaseBoundary,
}: MonthCellProps) {
  const previewCount = density === 'desktop' ? 3 : 2;
  const previews = entries.slice(0, previewCount);
  const overflow = Math.max(0, entries.length - previewCount);
  const day = parseInt(date.slice(8, 10), 10);

  return (
    <button
      type="button"
      onClick={() => onTap(date)}
      onDragOver={onCellDrop ? (e) => { e.preventDefault(); } : undefined}
      onDrop={onCellDrop ? (e) => {
        e.preventDefault();
        const id = e.dataTransfer.getData('text/entry-id');
        if (id) onCellDrop(date, id);
      } : undefined}
      aria-label={`${date}${isToday ? ', today' : ''}, ${entries.length} ${entries.length === 1 ? 'entry' : 'entries'}`}
      style={{
        position: 'relative',
        flex: 1,
        minHeight: 80,
        backgroundColor: isCurrentMonth ? admin.surface : admin.bg,
        opacity: isCurrentMonth ? 1 : 0.5,
        border: `${isToday ? 2 : 1}px solid ${isToday ? palette.accent : admin.border}`,
        padding: 4,
        cursor: 'pointer',
        textAlign: 'left',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        font: 'inherit',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{
          fontSize: 12, fontWeight: isToday ? 700 : 600,
          color: isToday ? palette.accent : (isCurrentMonth ? palette.g700 : palette.g400),
        }}>
          {day}
        </span>
        {entries.length > 0 ? (
          <span style={{ fontSize: 9, color: palette.g500 }}>{entries.length}</span>
        ) : null}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 2 }}>
        {previews.map((entry) => (
          <div
            key={entry.id}
            draggable={onCellDrop != null}
            onDragStart={(e) => {
              e.dataTransfer.setData('text/entry-id', entry.id);
              e.dataTransfer.effectAllowed = 'move';
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: 2,
              padding: '2px 4px', borderRadius: 3,
              backgroundColor: entryPillColor(entry),
            }}
          >
            <span style={{
              fontSize: 9, color: palette.white, flex: 1,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {entry.title || 'Untitled'}
            </span>
            {showRecurringBadges && entry.is_recurring ? (
              <RecurringBadge size={9} color={palette.white} />
            ) : null}
          </div>
        ))}
        {overflow > 0 ? (
          <span style={{ fontSize: 9, color: palette.g500, marginLeft: 4 }}>+{overflow}</span>
        ) : null}
      </div>

      {leaseBoundary ? <LeaseBoundaryBar kind={leaseBoundary} /> : null}
    </button>
  );
}
